import { useRef } from 'react';
import { CalendarDays } from 'lucide-react';

// A date field that always shows Australian format (DD/MM/YYYY), whatever language the browser or Mac is set to.
// Tapping it opens the device's own date picker. value / onChange use YYYY-MM-DD, like <input type="date">.
const canShowPicker = typeof HTMLInputElement !== 'undefined' && 'showPicker' in HTMLInputElement.prototype;

export default function DateInput({ value, onChange, min, max, className = '', style, ariaLabel = 'Date', placeholder = 'DD/MM/YYYY', clearable = false }: {
  value: string | null | undefined; onChange: (v: string) => void; min?: string; max?: string;
  className?: string; style?: React.CSSProperties; ariaLabel?: string; placeholder?: string; clearable?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const shown = value && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}` : '';
  const open = () => { const el = ref.current; if (!el) return; try { el.showPicker(); } catch { el.focus(); el.click(); } };
  return (
    <span role="button" tabIndex={0} aria-label={`${ariaLabel}${shown ? `: ${shown}` : ''}`} onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      className={`relative inline-flex items-center cursor-pointer select-none ${className}`} style={style}>
      <span className="flex-1 truncate tabular-nums" style={{ opacity: shown ? 1 : 0.5 }}>{shown || placeholder}</span>
      {clearable && shown
        ? <button type="button" onClick={(e) => { e.stopPropagation(); onChange(''); }} className="ml-2 text-[12px] opacity-60" aria-label="Clear date">✕</button>
        : <CalendarDays size={15} className="ml-2 shrink-0 opacity-60" />}
      {/* The real picker. Where showPicker isn't supported it sits on top (invisible) so a tap still opens it. */}
      <input ref={ref} type="date" value={value ?? ''} min={min} max={max} onChange={(e) => onChange(e.target.value)} tabIndex={-1} aria-hidden
        className="absolute inset-0 w-full h-full opacity-0" style={{ pointerEvents: canShowPicker ? 'none' : 'auto' }} />
    </span>
  );
}
